import * as React from 'react';
import type { Decorator } from '@storybook/react';
import type {
  EntityDetail,
  EntityDetailSchema,
  DomainEntity,
  TreeNode,
} from '@web-config-app/core';
import { mockCactusEntity, mockSimpleSchema } from '@web-config-app/mock-data';
import { CustomTestLeagueProvider } from '../test-helpers/custom-test-league-provider';
import {
  EntityDetailsProvider,
  EntityDetailsProviderProps,
} from '../components/entity-details-provider/entity-details-provider.component';
import { DataSourceStoreProvider } from '../state/data-source/data-source-store-provider';
import { getEntityDetailsProviderMockProps } from '../__mocks__/get-entity-details-provider-mock-props';

const operationsMock = {
  get: {},
  update: {},
} as unknown as EntityDetailsProviderProps['operations'];

const mockProviderProps = getEntityDetailsProviderMockProps();

/**
 * This decorator provides three layers of context that are needed to render stories that
 * include an EntityForm component.
 *
 * 1. EntityDetailsProvider - access {@link EntityDetailsProviderProps} via context
 * 2. CustomTestLeagueProvider - access to the test config domains and entities via context
 * 3. QueryClientProvider - required when the EntityForm includes an Entity Reference or Image control
 *
 * TODO: it might be possible to mock the QueryClientProvider? That'd be nicer
 *
 * To add to stories, import this component and pass it to the `decorators` array
 */

export const EntityFormProviderDecorator: Decorator<{
  data?: EntityDetail;
  schema?: EntityDetailSchema;
  rootSchema?: EntityDetailSchema;
  domainEntity?: DomainEntity;
  rootData?: EntityDetail;
  entityType?: string;
  initialPath?: string;
}> = (Story: any, { args }) => {
  const [path, setPath] = React.useState(args.initialPath ?? 'root');
  const [entityData, setEntityData] = React.useState<EntityDetail>(
    args.rootData ?? args.data ?? ({} as EntityDetail),
  );
  const onTreeNodeSelect = (item: TreeNode) => setPath(item.id);
  const entityType =
    args.entityType ??
    (args.rootSchema ?? args.schema)?.properties?.type?.enum?.[0];

  return (
    <CustomTestLeagueProvider>
      <DataSourceStoreProvider>
        <EntityDetailsProvider
          {...mockProviderProps}
          entityRootData={entityData}
          setEntityData={setEntityData}
          rootSchema={args?.rootSchema ?? args.schema ?? mockSimpleSchema}
          domainEntity={args?.domainEntity ?? mockCactusEntity}
          operations={operationsMock}
          onTreeNodeSelect={onTreeNodeSelect}
          entityType={entityType}
          path={path}
        >
          <Story />
        </EntityDetailsProvider>
      </DataSourceStoreProvider>
    </CustomTestLeagueProvider>
  );
};
